import React, { useContext, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { ModalPageNames } from "../../../consts";
import { AuthContext, IAuthContext } from "../../../contexts/auth/authContext";
import Form from "react-bootstrap/Form";
import { Code, Password, Email } from "../authComponents";
import {
   useValidCode,
   useValidPassword,
   useValidEmail,
} from "../../../hooks/useAuthValidationHook";
import {
   CheckIconImg,
   CrossIconImg,
   ResendButton,
   ResendButtonGroup,
   SubmitCancelGroup,
   VerificationForm,
   VerificationModalAlert,
   VerificationModalHeader,
} from "./styled";
import { FormGroupAlignedCenter } from "../styled";
import { useMediaQuery } from "react-responsive";

function VerificationComponent({
   emailToBeVerified,
   onClose,
   isPasswordSection,
   setPageName,
}) {
   const { code, setCode, codeIsValid } = useValidCode("");
   const { password, setPassword, passwordIsValid } = useValidPassword("");
   const { email, setEmail, emailIsValid } = useValidEmail(emailToBeVerified || "");
   const [passwordVisible, isPasswordVisible] = useState<boolean>(false);
   const [error, setError] = useState<string>("");
   const authContext: IAuthContext = useContext(AuthContext);
   const isMobile: boolean = useMediaQuery({ maxWidth: 767 });
   const isInValid = (): boolean => {
      if (isPasswordSection) {
         return (
            !codeIsValid ||
            code.length === 0 ||
            !passwordIsValid ||
            password.length === 0 ||
            (!emailToBeVerified && (!emailIsValid || email.length === 0))
         );
      }
      return !codeIsValid || code.length === 0 || (!emailToBeVerified && (!emailIsValid || email.length === 0));
   };

   const submitDetails = async (): Promise<void> => {
      // SCREDP-68: verify email is provided
      const emailToUse = emailToBeVerified || email;
      
      if (!emailToUse) {
         setError("Please provide a valid email address");
         return;
      }
      
      if (isPasswordSection) {
         await confirmPasswordFunc(emailToUse);
      } else {
         await verificationClicked(emailToUse);
      }
   };

   const verificationClicked = async (email): Promise<void> => {
      try {
         await authContext.verifyCode(email, code);
         setPageName(ModalPageNames.SIGN_IN);
      } catch (err: any) {
         // SCREDP-68: handle expired code
         if (err.code === "ExpiredCodeException" || (err.error && err.error.code === "ExpiredCodeException")) {
            setError("This verification code has expired.  Please request a new one using the link below.");
         } else
            setError(err.message);
      }
   };

   const confirmPasswordFunc = async (email): Promise<void> => {
      try {
         await authContext.confirmPassword(email, code, password);
         setPageName(ModalPageNames.SIGN_IN);
      } catch (err: any) {
         setError(err.message);
      }
   };

   const resendCodeFunc = async (): Promise<void> => {
      // SCREDP-68: verify email is provided
      const emailToUse = emailToBeVerified || email;
      
      if (!emailToUse) {
         setError("Please provide a valid email address first");
         return;
      }      

      if (isPasswordSection) {
         await initiateForgotPassword(emailToUse);
      } else {
         await resendCodeConfirmation(emailToUse);
      }
   };

   const resendCodeConfirmation = async (email): Promise<void> => {
      try {
         await authContext.resendCode(email);
      } catch (err: any) {
         setError(err.message);
      }
   };

   const initiateForgotPassword = async (email): Promise<void> => {
      try {
         await authContext.forgotPassword(email);
      } catch (err: any) {
         setError(err);
      }
   };

   const resendCode = (): void => {
      resendCodeFunc();
   };

   const handleClose = () => {
      onClose();
   };

   return (
      <>
         <Modal.Header></Modal.Header>
         <Modal.Body>
            <VerificationModalHeader>
               {emailToBeVerified && (
                     <h5>
                        <b>Almost there!</b>
                     </h5>
                  )}
            </VerificationModalHeader>
            {error && (
               <VerificationModalAlert variant="danger">
                  {error}
               </VerificationModalAlert>
            )}
            <VerificationForm
               className={isMobile ? "mobile-css" : "desktop-css"}
            >
               <FormGroupAlignedCenter className="mb-3">
                  <span>
                     {emailToBeVerified 
                           ? <>A six-digit verification code was sent to:<br/><b>{emailToBeVerified}</b></>
                           : <>Please provide your email and the most recent verification code. If you need another verification code, please click the link below:</>
                        }
                  </span>
               </FormGroupAlignedCenter>
               <ResendButtonGroup className="mb-3">
                  <ResendButton variant="link" onClick={resendCode}>
                     Resend Code
                  </ResendButton>
               </ResendButtonGroup>
               <Form.Group>
                  {/* SCREDP-68: verify email with verification code if emailToBeVerified is not provided */}
                  {!emailToBeVerified && (
                        <Email
                           emailIsValid={emailIsValid}
                           email={email}
                           setEmail={setEmail}
                           placeholderValue="Email Address"
                           isRequired={false}
                           autofocus={true}
                        />
                  )}
                  <Code codeIsValid={codeIsValid} 
                     setCode={setCode}
                     autofocus={true} // Set focus to the Code input (SCREDP-55)
                  />
               </Form.Group>
               {isPasswordSection && (
                  <Password
                     label="New Password"
                     passwordIsValid={passwordIsValid}
                     setPassword={setPassword}
                     passwordVisible={passwordVisible}
                     isPasswordVisible={isPasswordVisible}
                     isRequired={false}
                     isPasswordShowRequired
                  />
               )}
               <SubmitCancelGroup className="mb-5">
                  <Button
                     onClick={handleClose}
                     variant="danger"
                     className="btn-css"
                  >
                     {" "}
                     Cancel <CrossIconImg />
                  </Button>
                  <Button
                     disabled={isInValid()}
                     onClick={submitDetails}
                     variant="danger"
                     className="btn-css"
                  >
                     {" "}
                     Submit <CheckIconImg />
                  </Button>
               </SubmitCancelGroup>
               <FormGroupAlignedCenter>
                  Can't find it? {isMobile && isPasswordSection && <br></br>}
                  Check your spam/junk folder.
               </FormGroupAlignedCenter>
            </VerificationForm>
         </Modal.Body>
      </>
   );
}

export default VerificationComponent;
